/**
 * RealtimeMapOSM - Carte temps réel des opérations avec OpenStreetMap/Leaflet
 * 
 * Version alternative de RealtimeMapSimple utilisant Leaflet au lieu de Mapbox GL.
 * Utilise le système multi-provider pour la configuration et le routing.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2, Filter, X, MapPin, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { getMapConfig, getLeafletConfig } from '@/lib/mapService';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Palette de couleurs pour les types de marchandise
const COLOR_PALETTE = [
  '#3b82f6', '#ef4444', '#22c55e', '#f97316', '#a855f7',
  '#eab308', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6',
];
const DEFAULT_COLOR = '#9ca3af';

const getColorForType = (type, allTypes) => {
  if (!type) return DEFAULT_COLOR;
  const index = allTypes.indexOf(type);
  return index === -1 ? DEFAULT_COLOR : COLOR_PALETTE[index % COLOR_PALETTE.length];
};

// Créer une icône colorée
const createColoredIcon = (color, label = 'A') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
      ">${label}</div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

/**
 * Composant pour ajuster la vue de la carte
 */
function MapBoundsController({ operations }) {
  const map = useMap();
  
  useEffect(() => {
    if (operations.length > 0) {
      const bounds = L.latLngBounds([]);
      
      operations.forEach(op => {
        if (op.depart?.coords) {
          bounds.extend([op.depart.coords.lat, op.depart.coords.lng]);
        }
        if (op.arrivee?.coords) {
          bounds.extend([op.arrivee.coords.lat, op.arrivee.coords.lng]);
        }
        if (op.etapes) {
          op.etapes.forEach(etape => {
            if (etape.coords?.lat && etape.coords?.lng) {
              bounds.extend([etape.coords.lat, etape.coords.lng]);
            }
          });
        }
      });
      
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [operations, map]);
  
  return null;
}

/**
 * Composant de carte temps réel avec OpenStreetMap
 */
export default function RealtimeMapOSM() {
  const [operations, setOperations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [routeCache, setRouteCache] = useState({});
  const [mapConfig, setMapConfig] = useState(null);
  
  // Filtres
  const [filters, setFilters] = useState({
    mode: 'both',
    client: '',
    typeMarchandise: '',
    jourUniquement: false
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  
  // Centre par défaut (Côte d'Ivoire)
  const defaultCenter = [7.54, -5.55];
  
  // Charger la configuration de la carte
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getMapConfig();
        setMapConfig(config);
      } catch (err) {
        console.error('Erreur config carte:', err);
      }
    }
    loadConfig();
  }, []);
  
  // Fonctions de fetch définies avant les useEffect qui les utilisent
  const fetchOperations = useCallback(async (since = null) => {
    try {
      const params = since ? `?since=${since}` : '';
      const response = await api.get(`/operations/realtime/en-cours${params}`);
      
      if (since) {
        setOperations(prev => {
          const newOps = response.data;
          const updated = [...prev];
          newOps.forEach(newOp => {
            const index = updated.findIndex(op => op.id_operation === newOp.id_operation);
            if (index >= 0) {
              updated[index] = newOp;
            } else {
              updated.push(newOp);
            }
          });
          return updated;
        });
      } else {
        setOperations(response.data);
      }
      
      setLastUpdate(new Date().toISOString());
      setLoading(false);
    } catch (err) {
      console.error('Erreur chargement opérations:', err);
      toast.error('Erreur de chargement des opérations');
      setLoading(false);
    }
  }, []);
  
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/operations/realtime/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  }, []);
  
  // Charger les opérations
  useEffect(() => {
    fetchOperations();
    fetchStats();
    
    const interval = setInterval(() => {
      if (autoRefresh) {
        fetchOperations(lastUpdate);
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, lastUpdate, fetchOperations, fetchStats]);
  
  // Filtrage des opérations
  const filteredOperations = useMemo(() => {
    return operations.filter(op => {
      if (filters.mode === 'intraland' && op.mode !== 'intraland') return false;
      if (filters.mode === 'interland' && op.mode !== 'interland') return false;
      if (filters.client && op.client.nom !== filters.client) return false;
      if (filters.typeMarchandise && op.type_marchandise !== filters.typeMarchandise) return false;
      if (filters.jourUniquement) {
        const today = new Date().toISOString().split('T')[0];
        const opDate = op.date_depart?.split('T')[0];
        if (opDate !== today) return false;
      }
      return true;
    });
  }, [operations, filters]);
  
  // Récupérer la route réelle via l'API
  const fetchRoute = useCallback(async (operationId, waypoints) => {
    if (routeCache[operationId]) {
      return routeCache[operationId];
    }
    
    try {
      const response = await api.post('/geo/multi-route', {
        waypoints: waypoints.map(wp => ({ lat: wp[0], lng: wp[1] })),
        profile: 'driving'
      });
      
      if (response.data.success && response.data.coordinates) {
        const coords = response.data.coordinates.map(c => [c[1], c[0]]); // [lng, lat] -> [lat, lng]
        setRouteCache(prev => ({ ...prev, [operationId]: coords }));
        return coords;
      }
    } catch (err) {
      console.error('Erreur récupération route:', err);
    }
    
    // Fallback: ligne droite
    return waypoints;
  }, [routeCache]);
  
  // Charger les routes pour toutes les opérations
  useEffect(() => {
    if (filteredOperations.length > 0) {
      filteredOperations.forEach(async (op) => {
        if (!routeCache[op.id_operation]) {
          const waypoints = [];
          waypoints.push([op.depart.coords.lat, op.depart.coords.lng]);
          
          if (op.etapes) {
            op.etapes
              .sort((a, b) => a.ordre - b.ordre)
              .forEach(etape => {
                if (etape.coords?.lat && etape.coords?.lng) {
                  waypoints.push([etape.coords.lat, etape.coords.lng]);
                }
              });
          }
          
          waypoints.push([op.arrivee.coords.lat, op.arrivee.coords.lng]);
          
          if (waypoints.length >= 2) {
            fetchRoute(op.id_operation, waypoints);
          }
        }
      });
    }
  }, [filteredOperations, routeCache, fetchRoute]);
  
  // Types de marchandise uniques
  const uniqueTypes = useMemo(() => 
    [...new Set(filteredOperations.map(op => op.type_marchandise).filter(Boolean))].sort(),
    [filteredOperations]
  );
  
  // Listes pour filtres
  const clientsList = useMemo(() => 
    [...new Set(operations.map(op => op.client?.nom).filter(Boolean))].sort(),
    [operations]
  );
  
  const typesMarchandise = useMemo(() =>
    [...new Set(operations.map(op => op.type_marchandise).filter(Boolean))].sort(),
    [operations]
  );
  
  const leafletConfig = mapConfig ? getLeafletConfig(mapConfig) : {
    tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
    subdomains: ['a', 'b', 'c']
  };
  
  if (loading) {
    return (
      <div className="relative h-full w-full min-h-[600px] flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg p-6 shadow-xl">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <span className="text-gray-600">Chargement de la carte...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative h-full w-full min-h-[600px]">
      {/* Carte Leaflet */}
      <MapContainer
        center={defaultCenter}
        zoom={6.5}
        style={{ height: '100%', width: '100%', minHeight: '600px' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution={leafletConfig.attribution}
          url={leafletConfig.tileUrl}
          maxZoom={leafletConfig.maxZoom}
          subdomains={leafletConfig.subdomains}
        />
        
        {/* Contrôleur de bounds */}
        <MapBoundsController operations={filteredOperations} />
        
        {/* Routes et marqueurs pour chaque opération */}
        {filteredOperations.map((op, index) => {
          const color = getColorForType(op.type_marchandise, uniqueTypes);
          const routeCoords = routeCache[op.id_operation] || [
            [op.depart.coords.lat, op.depart.coords.lng],
            [op.arrivee.coords.lat, op.arrivee.coords.lng]
          ];
          
          return (
            <React.Fragment key={op.id_operation || index}>
              {/* Ligne de route */}
              <Polyline
                positions={routeCoords}
                pathOptions={{
                  color: color,
                  weight: 5,
                  opacity: 0.8
                }}
                eventHandlers={{
                  click: () => setSelectedOperation(op),
                  mouseover: (e) => {
                    e.target.setStyle({ weight: 8 });
                  },
                  mouseout: (e) => {
                    e.target.setStyle({ weight: 5 });
                  }
                }}
              />
              
              {/* Marqueur départ */}
              <Marker
                position={[op.depart.coords.lat, op.depart.coords.lng]}
                icon={createColoredIcon(color, 'A')}
              >
                <Popup>
                  <div className="text-sm p-1">
                    <strong className="text-green-600">Départ</strong><br />
                    {op.depart.ville}<br />
                    <span className="text-gray-500">{op.client?.nom}</span>
                  </div>
                </Popup>
              </Marker>
              
              {/* Marqueurs étapes */}
              {op.etapes?.map((etape, idx) => (
                etape.coords?.lat && etape.coords?.lng && (
                  <CircleMarker
                    key={`etape-${op.id_operation}-${idx}`}
                    center={[etape.coords.lat, etape.coords.lng]}
                    radius={8}
                    pathOptions={{
                      color: 'white',
                      fillColor: color,
                      fillOpacity: 0.9,
                      weight: 2
                    }}
                  >
                    <Popup>
                      <div className="text-sm p-1">
                        <strong className="text-orange-600">Étape {etape.ordre}</strong><br />
                        {etape.ville}
                      </div>
                    </Popup>
                  </CircleMarker>
                )
              ))}
              
              {/* Marqueur arrivée */}
              <Marker
                position={[op.arrivee.coords.lat, op.arrivee.coords.lng]}
                icon={createColoredIcon(color, 'B')}
              >
                <Popup>
                  <div className="text-sm p-1">
                    <strong className="text-red-600">Arrivée</strong><br />
                    {op.arrivee.ville}<br />
                    <span className="text-gray-500">{op.distance_estimee} km</span>
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          );
        })}
      </MapContainer>
      
      {/* Header avec stats */}
      <div className="absolute top-4 left-4 right-4 z-[1000] flex gap-4 pointer-events-none">
        <Card className="bg-white/95 backdrop-blur shadow-lg pointer-events-auto">
          <CardContent className="p-4">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs text-gray-500">Opérations en cours</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {filteredOperations.length}
                </p>
              </div>
              {stats && (
                <>
                  <div className="border-l pl-4">
                    <p className="text-xs text-gray-500">Intraland</p>
                    <p className="text-lg font-semibold">{stats.intraland}</p>
                  </div>
                  <div className="border-l pl-4">
                    <p className="text-xs text-gray-500">Interland</p>
                    <p className="text-lg font-semibold">{stats.interland}</p>
                  </div>
                </>
              )}
              <div className="border-l pl-4 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <Filter className="w-4 h-4" />
                  Filtres
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchOperations()}
                  className="flex items-center gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                {mapConfig?.provider === 'openstreetmap' ? 'OSM' : 'Mapbox'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Panneau de filtres */}
      {showFilters && (
        <Card className="absolute top-24 left-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-80">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Filtres</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs text-gray-600">Affichage</Label>
              <Select value={filters.mode} onValueChange={(val) => setFilters({...filters, mode: val})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Intraland + Interland</SelectItem>
                  <SelectItem value="intraland">Intraland uniquement</SelectItem>
                  <SelectItem value="interland">Interland uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {clientsList.length > 0 && (
              <div>
                <Label className="text-xs text-gray-600">Client</Label>
                <Select value={filters.client || "all"} onValueChange={(val) => setFilters({...filters, client: val === "all" ? "" : val})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tous les clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les clients</SelectItem>
                    {clientsList.map(client => (
                      <SelectItem key={client} value={client}>{client}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {typesMarchandise.length > 0 && (
              <div>
                <Label className="text-xs text-gray-600">Type de marchandise</Label>
                <Select value={filters.typeMarchandise || "all"} onValueChange={(val) => setFilters({...filters, typeMarchandise: val === "all" ? "" : val})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {typesMarchandise.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="jour-uniquement" 
                checked={filters.jourUniquement}
                onCheckedChange={(checked) => setFilters({...filters, jourUniquement: checked})}
              />
              <Label htmlFor="jour-uniquement" className="text-sm cursor-pointer">
                Opérations du jour uniquement
              </Label>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({
                mode: 'both',
                client: '',
                typeMarchandise: '',
                jourUniquement: false
              })}
              className="w-full"
            >
              Réinitialiser
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Légende */}
      <Card className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-64">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Légende</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLegend(!showLegend)}
              className="h-6 w-6 p-0"
            >
              {showLegend ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        {showLegend && (
          <CardContent className="space-y-2 max-h-96 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-700 mb-2">Opérations en cours</p>
            {filteredOperations.length > 0 ? (
              filteredOperations.slice(0, 10).map((op, index) => {
                const color = getColorForType(op.type_marchandise, uniqueTypes);
                return (
                  <div 
                    key={op.id_operation || index} 
                    className="flex items-start gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => setSelectedOperation(op)}
                  >
                    <div 
                      className="w-3 h-3 rounded-full mt-0.5 flex-shrink-0" 
                      style={{ backgroundColor: color }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">
                        {op.numero_operation || op.id_operation}
                      </p>
                      <p className="text-xs text-gray-600 truncate">
                        {op.client?.nom || 'Client inconnu'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {op.depart?.ville} → {op.arrivee?.ville}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-gray-500 italic">Aucune opération en cours</p>
            )}
            {filteredOperations.length > 10 && (
              <p className="text-xs text-gray-400 text-center">
                +{filteredOperations.length - 10} autres...
              </p>
            )}
            <div className="border-t pt-2 mt-2">
              <p className="text-xs text-gray-500">
                <span className="font-semibold">A</span> = Départ •
                <span className="font-semibold">B</span> = Arrivée
              </p>
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Panneau de détails */}
      {selectedOperation && (
        <Card className="absolute top-4 right-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-96">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {selectedOperation.numero_operation || selectedOperation.id_operation}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedOperation(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Client</p>
              <p className="font-medium">{selectedOperation.client?.nom}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">Type marchandise</p>
                <p className="font-medium">{selectedOperation.type_marchandise}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Poids</p>
                <p className="font-medium">{selectedOperation.poids} T</p>
              </div>
            </div>
            
            <div>
              <p className="text-xs text-gray-500">Trajet</p>
              <p className="font-medium">
                {selectedOperation.depart?.ville} → {selectedOperation.arrivee?.ville}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedOperation.distance_estimee} km
              </p>
            </div>
            
            <div>
              <p className="text-xs text-gray-500">Statut</p>
              <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-emerald-100 text-emerald-700">
                {selectedOperation.statut}
              </span>
            </div>
            
            <Button 
              className="w-full mt-2"
              onClick={() => {
                window.location.href = `/operations?search=${selectedOperation.id_operation}`;
              }}
            >
              Voir l&apos;opération
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Message si aucune opération */}
      {filteredOperations.length === 0 && !loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[500]">
          <Card className="bg-white/95 backdrop-blur shadow-lg pointer-events-auto">
            <CardContent className="p-6 text-center">
              <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">
                Aucune opération en cours à afficher
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Les opérations doivent avoir des coordonnées géographiques
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
